import type { RoundPhase } from '@uefadigital/panenka';
import { isNumber } from '@uefadigital/panenka';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { loadEntitiesByClassification } from '@apis/editorial';
import type { EditorialContent } from '@cobalt/utilities';
import { hydrateWithTranslations } from '@services/TranslatorService';
import type { Match } from '@shared-types/match.types';
import type { CityMatch, MatchWithTickets, ScheduleMatch, TicketingInfo } from '@shared-types/ticketing.types';
import { DEMAND_LEVEL } from '@shared-types/ticketing.types';

export const demandLevelConfig = {
  [DEMAND_LEVEL.LOW]: { color: 'support-01', size: 80 },
  [DEMAND_LEVEL.MEDIUM]: { color: 'support-02', size: 60 },
  [DEMAND_LEVEL.HIGH]: { color: 'support-03', size: 40 },
  [DEMAND_LEVEL.VERY_HIGH]: { color: 'support-04', size: 20 },
  [DEMAND_LEVEL.NOT_AVAILABLE]: { color: 'ui-04', size: 100 },
} as const;
const currencyConfig = {
  CHF: {
    symbol: 'CHF',
    cmsSymbols: ['CHF'],
    symbolPosition: 'before',
    decimalDigits: 2,
    localFormat: 'en-US',
  },
  '€': {
    symbol: '€',
    cmsSymbols: ['EUR', 'Euro'],
    symbolPosition: 'before',
  },
  $: {
    symbol: '$',
    cmsSymbols: ['Dollar'],
    symbolPosition: 'before',
  },
  '£': {
    symbol: '£',
    cmsSymbols: ['Pound'],
    symbolPosition: 'before',
  },
} as const;

export const getTicketingData = async (competitionId: string, seasonYear: string, phase: RoundPhase) => {
  const params = {
    type: 'ticket',
    limit: 100,
    offset: 0,
    ['param.attributes.competitions']: competitionId,
    ['param.attributes.main.season']: seasonYear,
    ['param.attributes.main.phase']: phase,
  };
  const response = await loadEntitiesByClassification(params, 'en');
  return manipulateTicketingData(response);
};
const manipulateTicketingData = (response: EditorialContent[]): TicketingInfo[] =>
  response
    ?.flatMap((node) => {
      const match = node.relations?.find((elm) => elm.match)?.match as {
        matchId: string;
      };
      if (!match) return null;
      return node.externalProduct.ticketing.map((ticket) => ({ ...ticket, matchId: match.matchId }));
    })
    .filter(Boolean);

export const hydrateMatchWithTickets =
  (ticketingData: TicketingInfo[]) =>
  (match: Match): MatchWithTickets => ({
    ...match,
    tickets: ticketingData.filter((t) => t.matchId === match?.id),
  });

export const groupMatchesByCity = (matches: MatchWithTickets[]) =>
  matches
    ?.reduce<CityMatch[]>((allCities, match) => {
      const key = match.stadium.id;

      const index = allCities.findIndex((c) => c.stadium.id === key);
      if (index > -1) {
        allCities[index].matches.push(match);
      } else {
        allCities.push({
          stadium: hydrateWithTranslations(match.stadium),
          city: hydrateWithTranslations(match.stadium.city),
          matches: [match],
        });
      }

      return allCities;
    }, [])
    .toSorted((a, b) => a.city.name.localeCompare(b.city.name));

export const groupMatchesByRound = (matches: MatchWithTickets[]) =>
  matches?.reduce<ScheduleMatch[]>((scheduleMatches, match) => {
    const roundIndex = scheduleMatches.findIndex((r) => r.round.id === match.round.id);
    if (roundIndex > -1) {
      scheduleMatches[roundIndex].matches.push(match);
    } else {
      scheduleMatches.push({
        round: hydrateWithTranslations(match.round),
        matches: [match],
      });
    }

    return scheduleMatches;
  }, []);

export const getCurrencySymbol = (matches: MatchWithTickets[]): string => {
  const noTicketsAvailable = !matches || !matches[0] || !matches[0].tickets?.length;
  if (noTicketsAvailable) return '';

  return getCurrencySymbolFromTicket(matches[0].tickets[0]);
};

export const getCurrencySymbolFromTicket = (ticket: TicketingInfo): string => {
  if (!ticket?.ticketCurrency) return '';

  const currencyKey = Object.keys(currencyConfig).find((key) =>
    currencyConfig[key]?.cmsSymbols?.includes(ticket.ticketCurrency),
  );

  return currencyKey ? currencyConfig[currencyKey].symbol : '';
};
export const getTicketPriceFormatted = (ticket: TicketingInfo): string => {
  if (!ticket) return '';
  const currencySymbol = getCurrencySymbolFromTicket(ticket);
  const spacer = currencySymbol ? ' ' : '';

  const _currencyConfig = currencyConfig[ticket.ticketCurrency];
  if (_currencyConfig?.localFormat) {
    const value = Number(ticket.ticketPrice);
    const stringFormatValue = new Intl.NumberFormat(_currencyConfig?.localFormat, {
      minimumFractionDigits: _currencyConfig?.decimalDigits,
      maximumFractionDigits: 2,
    }).format(value);
    return `${currencySymbol}${spacer}${stringFormatValue}`;
  }
  return `${currencySymbol}${spacer}${ticket.ticketPrice}`;
};

export const getMinPrice = (matches: MatchWithTickets[]) => {
  const prices = matches.flatMap((m) => m.tickets?.map((t) => +t.ticketPrice)).filter(isNumber);
  if (!prices.length) return null;
  return Math.min(...prices);
};

export const usePriceRange = (matches: MatchWithTickets[], category: string) =>
  useMemo(() => {
    const currency = getCurrencySymbol(matches);
    const prices = matches
      .flatMap((m) => m.tickets?.filter((tt) => tt.ticketType === category).map((t) => +t.ticketPrice))
      .filter(isNumber);
    if (!prices.length) return null;
    const minValue = Math.min(...prices);
    const maxValue = Math.max(...prices);
    return minValue === maxValue ? `${minValue} ${currency}` : `${minValue}${currency}  - ${maxValue}${currency}`;
  }, [matches, category]);

export const getDateLabel = (dateFrom: string, dateTo: string, customDateFormat: string) => {
  const from = dayjs(dateFrom, customDateFormat);
  const to = dayjs(dateTo, customDateFormat);
  if (from.isSame(to, 'date')) return `${from.format('D MMMM YYYY')}`;

  if (from.isSame(to, 'month')) return `${from.format('D')} - ${to.format('D MMMM YYYY')}`;

  return `${from.format('D MMMM')} - ${to.format('D MMMM YYYY')}`;
};
